
import { Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type PermissionState = 'granted' | 'denied' | 'prompt';

interface PermissionAlertProps {
  micPermission: PermissionState;
  isInitializingDevices: boolean;
  onRequestPermissions: () => void;
  compact?: boolean;
}

export const PermissionAlert = ({ micPermission, isInitializingDevices, onRequestPermissions, compact = false }: PermissionAlertProps) => {
  // Compact version - just an icon with tooltip
  if (compact) {
    if (micPermission === 'prompt') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                onClick={onRequestPermissions} 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 text-amber-500 hover:text-amber-600"
                disabled={isInitializingDevices}
              >
                <Mic className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Microphone permission needed for calls</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    
    if (micPermission === 'denied') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                onClick={onRequestPermissions} 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 text-red-500 hover:text-red-600"
                disabled={isInitializingDevices}
              >
                <MicOff className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Microphone access denied - click to request again</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    
    return null;
  }

  // Full version for when compact is false
  if (micPermission === 'denied') {
    return (
      <Alert variant="destructive" className="mb-4">
        <MicOff className="h-4 w-4" />
        <AlertTitle>Microphone access denied</AlertTitle>
        <AlertDescription>
          To make calls, you need to grant microphone access in your browser settings.
          <Button 
            onClick={onRequestPermissions} 
            variant="outline" 
            size="sm" 
            className="mt-2 w-full"
            disabled={isInitializingDevices}
          >
            {isInitializingDevices ? "Checking..." : "Request Access"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  
  if (micPermission === 'prompt') {
    return (
      <Alert className="mb-4">
        <Mic className="h-4 w-4" />
        <AlertTitle>Microphone permission needed</AlertTitle>
        <AlertDescription>
          You'll need to allow microphone access when prompted to make calls.
        </AlertDescription>
      </Alert>
    );
  }
  
  return null;
};
